// ProfilePage.js (web)

import React, { useState, useEffect } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import { FREE_DAILY_SEARCHES } from '../config/quotas';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import NotificationBell from '../components/notifications/NotificationBell';
import {
  User,
  Save,
  Crown,
  Stethoscope,
  HeartPulse,
  Users,
  Activity,
  Sparkles,
  Lock,
  Shield,
  UserCircle,
  AlertCircle,
  Bell,
} from 'lucide-react';

const ProfilePage = () => {
  const { user, getUserProfile, updateUser, isTrial, isTrialExpired, trialCountdownLabel } = useAuth();
  const [loading, setLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    birth_year: 1990,
    profession: 'autre',
    profile_picture: '',
    avatar: {
      type: 'neutral',
      color: 'blue',
    },
  });

  const renderAvatarIcon = () => {
    switch (formData.avatar?.type) {
      case 'medical':
        return <Stethoscope className="w-10 h-10" />;
      case 'nurse':
        return <HeartPulse className="w-10 h-10" />;
      case 'helper':
        return <Users className="w-10 h-10" />;
      case 'doctor':
        return <Activity className="w-10 h-10" />;
      case 'modern':
        return <Sparkles className="w-10 h-10" />;
      default:
        return <User className="w-10 h-10" />;
    }
  };

  // ⭐ NOUVEAU : Fonction pour afficher le nom du profil
  const getProfileTypeLabel = (profileType) => {
    switch (profileType) {
      case 'non_medical':
        return 'Patient / Grand public';
      case 'infirmier':
        return 'Infirmier(ère) diplômé(e)';
      case 'medecin':
        return 'Médecin';
      default:
        return 'Non défini';
    }
  };

  // ⭐ NOUVEAU : Fonction pour afficher l'icône du profil
  const getProfileTypeIcon = (profileType) => {
    switch (profileType) {
      case 'non_medical':
        return <UserCircle className="w-5 h-5" />;
      case 'infirmier':
        return <Shield className="w-5 h-5" />;
      case 'medecin':
        return <Stethoscope className="w-5 h-5" />;
      default:
        return <User className="w-5 h-5" />;
    }
  };

  useEffect(() => {
    const loadProfile = async () => {
      if (user?.uid) {
        try {
          const profile = await getUserProfile(user.uid);
          if (profile) {
            setFormData({
              first_name: profile.first_name || '',
              last_name: profile.last_name || '',
              birth_year: profile.birth_year || 1990,
              profession: profile.profession || 'autre',
              profile_picture: profile.profile_picture || '',
              avatar: profile.avatar || {
                type: 'neutral',
                color: 'blue',
              },
            });
          }
        } catch (error) {
          console.error('Erreur chargement profil:', error);
        }
      }
    };
    loadProfile();
  }, [user, getUserProfile]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user?.uid) return;

    setLoading(true);
    try {
      // ⭐ SÉCURITÉ : Ne pas inclure profession dans la mise à jour
      // Car on utilise maintenant profile_type qui est verrouillé
      await updateDoc(doc(db, 'users', user.uid), {
        first_name: formData.first_name,
        last_name: formData.last_name,
        birth_year: formData.birth_year,
        profile_picture: formData.profile_picture,
        avatar: formData.avatar,
        updatedAt: serverTimestamp(),
      });

      toast.success('Profil mis à jour avec succès');

      if (updateUser) {
        updateUser({ ...user, ...formData });
      }
    } catch (error) {
      console.error('Erreur mise à jour Firestore:', error);
      toast.error('Erreur lors de la mise à jour du profil');
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const token = await auth.currentUser.getIdToken();

      const response = await fetch(
        'https://europe-west1-medaviceia.cloudfunctions.net/createPortalSession',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 404) {
        toast.error('Aucun abonnement actif trouvé');
        return;
      }
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      window.location.href = data.url;
    } catch (error) {
      console.error('Erreur portail Stripe:', error);
      toast.error("Erreur lors de l'ouverture du portail");
    } finally {
      setPortalLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto" data-testid="profile-page">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-gradient-to-br from-primary/20 to-primary/10 dark:from-blue-600/20 dark:to-blue-500/10 rounded-xl flex items-center justify-center shadow-lg">
              <User className="w-6 h-6 text-primary dark:text-blue-500" />
            </div>
            <div>
              <h1 className="text-3xl font-heading font-bold text-foreground dark:text-gray-100">
                Mon Profil
              </h1>
              <p className="text-muted dark:text-gray-400">
                Gérez vos informations personnelles et votre sécurité
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {/* Subscription Card */}
          <Card className="p-6 bg-gradient-to-br from-primary/5 to-accent/5 dark:from-blue-600/10 dark:to-green-500/10 border-primary/20 dark:border-blue-500/30 shadow-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-primary/30 to-primary/20 dark:from-blue-600/40 dark:to-blue-500/30 rounded-full flex items-center justify-center shadow-md">
                  <Crown className="w-7 h-7 text-primary dark:text-blue-400" />
                </div>
                <div>
                  <h3 className="text-lg font-heading font-semibold text-foreground dark:text-gray-100">
                    {/* 3 états : abonné · essai en cours · gratuit */}
                    {user?.subscription_type === 'premium'
                      ? 'Abonnement Premium'
                      : isTrial
                        ? `Essai Premium — ${trialCountdownLabel}`
                        : 'Abonnement Gratuit'}
                  </h3>
                  <p className="text-sm text-muted dark:text-gray-400">
                    {user?.subscription_type === 'premium'
                      ? 'Accès illimité à toutes les fonctionnalités'
                      : isTrial
                        ? 'Accès illimité à toutes les fonctionnalités'
                        : `${FREE_DAILY_SEARCHES} recherches par jour`}
                  </p>
                </div>
              </div>

              {user?.subscription_type === 'premium' ? (
                <Button
                  onClick={handleManageSubscription}
                  disabled={portalLoading}
                  variant="outline"
                  className="border-primary/30 text-primary hover:bg-primary/10 dark:border-blue-500/50 dark:text-blue-400 dark:hover:bg-blue-500/20 shadow-md"
                >
                  {portalLoading ? 'Ouverture…' : 'Gérer mon abonnement'}
                </Button>
              ) : (
                <Button
                  onClick={() => (window.location.href = '/pricing')}
                  className="bg-accent hover:bg-accent/90 dark:bg-green-600 dark:hover:bg-green-700 shadow-lg"
                >
                  {/* Volontairement sur subscription_type : pendant l'essai il n'y a
                      AUCUN abonnement à gérer (createPortalSession renverrait 404). */}
                  {isTrialExpired ? 'Reprendre avec Premium' : 'Passer à Premium'}
                </Button>
              )}
            </div>
          </Card>

          {/* ⭐ Carte de profil professionnel VERROUILLÉ */}
          <Card className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-200 dark:border-blue-800 shadow-lg">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-4 flex-1">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500/20 to-indigo-500/20 dark:from-blue-600/30 dark:to-indigo-600/30 rounded-xl flex items-center justify-center shadow-md flex-shrink-0">
                  {getProfileTypeIcon(user?.profile_type)}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-lg font-heading font-semibold text-foreground dark:text-gray-100">
                      Profil professionnel
                    </h3>
                    <div className="flex items-center gap-1.5 px-2 py-1 bg-blue-100 dark:bg-blue-900/40 rounded-full">
                      <Lock className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                      <span className="text-xs font-medium text-blue-700 dark:text-blue-300">
                        Verrouillé
                      </span>
                    </div>
                  </div>
                  <p className="text-base font-medium text-blue-900 dark:text-blue-200 mb-2">
                    {getProfileTypeLabel(user?.profile_type)}
                  </p>
                  <div className="flex items-start gap-2 p-3 bg-blue-100/50 dark:bg-blue-900/30 rounded-lg border border-blue-200/50 dark:border-blue-800/50">
                    <AlertCircle className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-blue-800 dark:text-blue-300 leading-relaxed">
                      Votre profil professionnel a été défini lors de votre inscription et ne peut
                      plus être modifié. Cela garantit la cohérence de votre expérience et la
                      sécurité de la plateforme.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* 🆕 NOUVELLE SECTION: Notifications Forum */}
          <Card className="p-6 bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border-blue-200 dark:border-blue-800 shadow-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500/20 to-purple-500/20 dark:from-blue-600/30 dark:to-purple-600/30 rounded-xl flex items-center justify-center shadow-md">
                  <Bell className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h3 className="text-lg font-heading font-semibold text-foreground dark:text-gray-100">
                    Notifications du forum
                  </h3>
                  <p className="text-sm text-muted dark:text-gray-400">
                    Restez informé des réponses sur vos sujets suivis
                  </p>
                </div>
              </div>
              <NotificationBell />
            </div>
          </Card>

          {/* Profile Form */}
          <Card className="p-8 bg-white dark:bg-gray-900 border-slate-200 dark:border-gray-800 shadow-xl">
            <div className="mb-6">
              <h2 className="text-xl font-heading font-semibold text-foreground dark:text-gray-100 flex items-center gap-2">
                <User className="w-5 h-5 text-primary dark:text-blue-500" />
                Informations personnelles
              </h2>
              <p className="text-sm text-muted dark:text-gray-400 mt-1">
                Mettez à jour vos informations de profil
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Avatar */}
              <div className="flex justify-center p-6 bg-gradient-to-br from-slate-50 to-slate-100 dark:from-gray-800 dark:to-gray-850 rounded-xl border border-slate-200 dark:border-gray-700">
                <Avatar className="w-24 h-24 border-4 border-primary/20 dark:border-blue-500/30 bg-primary/10 dark:bg-blue-600/20 shadow-lg">
                  <AvatarFallback className="flex items-center justify-center text-primary dark:text-blue-400">
                    {renderAvatarIcon()}
                  </AvatarFallback>
                </Avatar>
              </div>

              {/* Avatar Selection */}
              <div className="p-6 bg-gradient-to-br from-slate-50 to-slate-100 dark:from-gray-800 dark:to-gray-850 rounded-xl border border-slate-200 dark:border-gray-700">
                <Label className="text-foreground dark:text-gray-200 text-base font-semibold">
                  Choisir un avatar
                </Label>
                <p className="text-xs text-muted dark:text-gray-400 mt-1 mb-4">
                  Sélectionnez une icône pour votre profil
                </p>

                <div className="grid grid-cols-3 sm:grid-cols-6 gap-4">
                  {[
                    { type: 'neutral', icon: <User />, label: 'Neutre' },
                    { type: 'medical', icon: <Stethoscope />, label: 'Médical' },
                    { type: 'nurse', icon: <HeartPulse />, label: 'Infirmier' },
                    { type: 'helper', icon: <Users />, label: 'Aidant' },
                    { type: 'doctor', icon: <Activity />, label: 'Docteur' },
                    { type: 'modern', icon: <Sparkles />, label: 'Moderne' },
                  ].map((item) => (
                    <div key={item.type} className="flex flex-col items-center gap-2">
                      <div
                        onClick={() =>
                          setFormData({
                            ...formData,
                            avatar: { type: item.type },
                          })
                        }
                        className={`
                          flex items-center justify-center
                          w-16 h-16 rounded-xl cursor-pointer
                          border-2 transition-all duration-200 shadow-md
                          ${
                            formData.avatar?.type === item.type
                              ? 'border-primary bg-primary/10 dark:border-blue-500 dark:bg-blue-600/20 scale-110'
                              : 'border-slate-200 dark:border-gray-700 hover:border-primary/40 dark:hover:border-blue-500/40 hover:scale-105'
                          }
                        `}
                      >
                        <div
                          className={`${formData.avatar?.type === item.type ? 'text-primary dark:text-blue-400' : 'text-slate-600 dark:text-gray-400'} w-6 h-6`}
                        >
                          {item.icon}
                        </div>
                      </div>
                      <span className="text-xs text-muted dark:text-gray-400 text-center">
                        {item.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Name Fields */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="first-name" className="dark:text-gray-200">
                    Prénom
                  </Label>
                  <Input
                    id="first-name"
                    data-testid="name-input"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    required
                    className="mt-2 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-200"
                  />
                </div>
                <div>
                  <Label htmlFor="last-name" className="dark:text-gray-200">
                    Nom
                  </Label>
                  <Input
                    id="last-name"
                    data-testid="last-name-input"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    required
                    className="mt-2 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-200"
                  />
                </div>
              </div>

              {/* Birth Year */}
              <div>
                <Label htmlFor="birth-year" className="dark:text-gray-200">
                  Année de naissance
                </Label>
                <Input
                  id="birth-year"
                  data-testid="birth-year-input"
                  type="number"
                  min="1920"
                  max={new Date().getFullYear()}
                  value={formData.birth_year}
                  onChange={(e) =>
                    setFormData({ ...formData, birth_year: parseInt(e.target.value) })
                  }
                  required
                  className="mt-2 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-200"
                />
              </div>

              {/* Email (read-only) */}
              <div>
                <Label className="dark:text-gray-200">Email</Label>
                <Input
                  value={user?.email || ''}
                  disabled
                  className="mt-2 bg-slate-100 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400"
                />
                <p className="text-xs text-muted dark:text-gray-400 mt-1">
                  L'email ne peut pas être modifié
                </p>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                data-testid="save-profile-button"
                disabled={loading}
                className="w-full bg-primary hover:bg-primary/90 dark:bg-blue-600 dark:hover:bg-blue-700 text-white shadow-lg shadow-primary/20 dark:shadow-blue-500/20"
              >
                <Save className="w-4 h-4 mr-2" />
                {loading ? 'Enregistrement...' : 'Enregistrer les modifications'}
              </Button>
            </form>
          </Card>

        </div>
      </div>
    </DashboardLayout>
  );
};

export default ProfilePage;
